import { z } from "zod";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";

export type ApplySourceType =
    | ScheduleSourceType.invoice
    | ScheduleSourceType.manual;

export interface ScheduleApplyFormValues {
    entityId: number | null;
    invoiceId: string | null;
    sourceLineIds: string[];
    manualSourceLabel: string;
    amount: number;
    termStart: string;
    specificDate: string;
    /** The picked invoice's date: the day it bills the scheduled amount. */
    sourceDate: string;
}

export function getInitialScheduleApplyValues(
    entityId: number | null,
): ScheduleApplyFormValues {
    return {
        entityId,
        invoiceId: null,
        sourceLineIds: [],
        manualSourceLabel: "",
        amount: 0,
        termStart: "",
        specificDate: "",
        sourceDate: "",
    };
}

export function buildScheduleApplyFormSchema(options: {
    sourceType: ApplySourceType | null;
    requiresSpecificDate: boolean;
}) {
    return z.object({
        entityId: z.number(),
        invoiceId:
            options.sourceType === ScheduleSourceType.invoice
                ? z.string()
                : z.string().nullable(),
        sourceLineIds: z.array(z.string()),
        manualSourceLabel:
            options.sourceType === ScheduleSourceType.manual
                ? z.string().trim().min(1)
                : z.string(),
        amount: z.number().positive(),
        termStart: z.string().min(1),
        specificDate: options.requiresSpecificDate
            ? z.string().min(1)
            : z.string(),
    });
}

export function buildScheduleApplyPayload(options: {
    values: ScheduleApplyFormValues;
    sourceType: ApplySourceType | null;
}): ApplyScheduleTemplateBody | null {
    const { values, sourceType } = options;
    if (values.entityId == null || sourceType == null) {
        return null;
    }
    const common = {
        entityId: values.entityId,
        amount: values.amount,
        termStart: values.termStart,
        specificDate: values.specificDate || undefined,
    };
    if (sourceType === ScheduleSourceType.invoice && values.invoiceId != null) {
        return {
            ...common,
            sourceType: ScheduleSourceType.invoice,
            invoiceId: values.invoiceId,
            sourceLineIds:
                values.sourceLineIds.length > 0
                    ? values.sourceLineIds
                    : undefined,
        };
    }
    if (sourceType === ScheduleSourceType.manual) {
        return {
            ...common,
            sourceType: ScheduleSourceType.manual,
            manualSourceLabel: values.manualSourceLabel.trim(),
        };
    }
    return null;
}
